import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { PageLoadingFullscreen } from '../../components/general/PageLoadingFullscreen';
import { trpc } from '../../trpc';
import { content } from './BackendReachable.content';
import { ConnectToVpnWarning } from './ConnectVpnWarning';

const BACKEND_REACHABLE_DELAY_DEFAULT = 20_000;

export const BackendReachable = ({
  children,
  backendReachableDelay,
}: PropsWithChildren & { backendReachableDelay?: number }) => {
  const [hasBeenIdleForLongTime, setHasBeenIdleForLongTime] = useState(false);
  const { inProgress } = useMsal();
  const isAuthenticating = inProgress !== InteractionStatus.None;

  useEffect(() => {
    setTimeout(() => {
      setHasBeenIdleForLongTime(true);
    }, backendReachableDelay ?? BACKEND_REACHABLE_DELAY_DEFAULT);
  }, [backendReachableDelay]);

  /*
   * This ping serves two purposes:
   * - start backendLambda on app open to speed up first search results
   * - determine if the backend is reachable at all
   *   - this way, we can offer the user some troubleshooting ideas for the most common failures, like not being logged into VPN
   */
  const ping = trpc.ping.useQuery(undefined, {
    enabled: !isAuthenticating,
  });

  if (isAuthenticating) {
    return <PageLoadingFullscreen msg={content.AUTHENTICATING} />;
  } else if (ping.isError && ping.error.data?.code !== 'UNAUTHORIZED') {
    return <ConnectToVpnWarning message={content.SERVER_ERROR} />;
  } else if (ping.isError && ping.error.data?.code === 'UNAUTHORIZED') {
    return <ConnectToVpnWarning message={content.ACCOUNT_AUTHORIZATION_ERROR} />;
  } else if (ping.isLoading && hasBeenIdleForLongTime) {
    return <ConnectToVpnWarning message={content.INITIAL_CONNECTION_TIMEOUT} />;
  } else if (!ping.isSuccess && !ping.isError) {
    return <PageLoadingFullscreen msg={content.LOADING} />;
  }

  return <>{children}</>;
};
